import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  checkExistingAccount,
  createRecipient,
  getCustomerTypes,
  getUserRecipient,
  updateUserRecipient,
  getBankList,
} from "../../../utils/Api";
import Bank_list from "../../../utils/Bank_list";
import { account_types_list } from "../../../utils/account_type";
import countryOptions from "../../../utils/AllCountryList";
import * as Yup from "yup";
import clsx from "clsx";
import { NavLink } from "react-router-dom";
import { MdOutlineKeyboardBackspace } from "react-icons/md";
import { Form } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import Select, { components } from "react-select";
import Autocomplete from "react-google-autocomplete";
import { toast } from "react-toastify";
import { getSelectedStreet, inputValidator } from "../../../hooks/validator";
import limits from "../../../hooks/limitations";

// Static country list
const countryList = [
  { name: "Australia", code: "AU", dialCode: "61" },
  { name: "Brazil", code: "BR", dialCode: "55" },
  { name: "China", code: "CN", dialCode: "86" },
  { name: "Ghana", code: "GH", dialCode: "233" },
  { name: "Kenya", code: "KE", dialCode: "254" },
  { name: "New Zealand", code: "NZ", dialCode: "64" },
  { name: "Nigeria", code: "NG", dialCode: "234" },
  { name: "Philippines", code: "PH", dialCode: "63" },
  { name: "Thailand", code: "TH", dialCode: "66" },
  { name: "Vietnam", code: "VN", dialCode: "84" },
];

const validator = Yup.object().shape({
  account_type: Yup.string().required(),
  business_name: Yup.string()
    .min(limits.business_name.min)
    .max(limits.business_name.max)
    .trim()
    .notOneOf([" "])
    .when("account_type", {
      is: "business",
      then: (schema) => schema.required("Business Name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  bank_name: Yup.string()
    .min(limits.bank.min, "Minimum 3 symbols")
    .max(limits.bank.max, "Maximum 50 symbols")
    .required("Bank name is required")
    .trim(),
  bank_identifier: Yup.string()
    .max(limits.bank_identifier, "Maximum 40 symbols")
    .notRequired()
    .trim(),
  other_name: Yup.string()
    .min(limits.bank.min)
    .max(limits.bank.max)
    .test("value-test", (value, validationcontext) => {
      const {
        createError,
        parent: { bank_name },
      } = validationcontext;
      if (
        bank_name === "other" &&
        (value?.length < 3 ||
          value === undefined ||
          value === null ||
          value === " ")
      ) {
        return createError({ message: "Please enter bank name" });
      } else {
        return true;
      }
    })
    .trim(),
  account_number: Yup.string()
    .min(limits.account_number.min)
    .max(limits.account_number.max)
    .required(),
  address: Yup.string().trim(),
  first_name: Yup.string()
    .max(limits.name)
    .trim()
    .when("account_type", {
      is: "individual",
      then: (schema) => schema.required("First Name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

  last_name: Yup.string()
    .max(limits.name)
    .trim()
    .when("account_type", {
      is: "individual",
      then: (schema) => schema.required("Last Name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

  middle_name: Yup.string().max(limits.name).notRequired().trim(),
  email: Yup.string()
    .matches(/^[\w-+\.]+@([\w-]+\.)+[\w-]{2,5}$/, "Invalid email format")
    .max(limits.email.max)
    .notRequired(),
  flat: Yup.string().max(limits.flat).notRequired(),
  building: Yup.string().max(limits.building).required().trim(),
  street: Yup.string().max(limits.street).required().trim(),
  city: Yup.string().max(limits.city).required().trim(),
  postcode: Yup.string()
    .min(
      limits.user_zip?.min || 4,
      `Postcode must be at least ${limits.user_zip?.min || 4} characters`
    )
    .max(
      limits.user_zip?.max || 9,
      `Postcode must be at most ${limits.user_zip?.max || 9} characters`
    )
    .trim(),
  state: Yup.string().max(limits.state).required().trim(),
  country: Yup.string().max(limits.country).required(),
  country_code: Yup.string(),
});

const RecipientForm = ({
  section = "add-edit",
  nextAction = () => { },
  backHandler = () => { },
  currency = null,
}) => {
  const navigate = useNavigate();
  const isAfrican = true;
  // fetching id if passed in route state
  const state = useLocation()?.state;
  const id = state?.id || false;
  // prev account number of recipient;
  const [prev_account_number, setPrevAcc] = useState("");
  // existing account error state
  const [is_account_existing, setExisting] = useState(false);
  // loader state
  const [loading, setLoading] = useState(false);
  // state handler for account type list
  const [account_types, setAccountTypes] = useState(account_types_list);
  const [bankList, setBankList] = useState([]);
  const [selectedBankCode, setSelectedBankCode] = useState("");
  const [loadingBanks, setLoadingBanks] = useState(true);
  const [PrevEmail, setPrevEmail] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  // initial fields object;
  const fields = {
    account_type: "individual", // required
    business_name: "", // required if account_type is business
    bank_name: null, // required
    other_name: "", // required if bank_name is other
    account_number: "", // required
    bank_identifier: "",
    first_name: "", // required
    last_name: "", // required
    middle_name: "", // not-required
    email: "", //optional
    flat: "", // not-required
    building: "", // required
    street: "", // required
    city: "", // required
    postcode: "", // required
    state: "", // required
    country: "Australia", // required
    country_code: "AU", // required
    address: "", // required
  };

  // formik initiailizer for form validation and submission;
  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    setValues,
    errors,
    touched,
    setFieldValue,
  } = useFormik({
    initialValues: fields,
    validationSchema: validator,
    onSubmit: async (values) => {
      setLoading(true);
      let payload = { ...values };

      if (currency === "NGN" && selectedBankCode) {
        payload.bank_code = selectedBankCode;
      } else {
        const bank = bankList.filter((bank) => {
          return bank.label === values.bank_name
        })
        if (bank.length > 0) {
          console.log(bank[0].code);
          payload.bank_code = bank[0].code;
        }
        else
          payload.bank_code = ""
      }

      if (payload.account_type !== "business") {
        for (const key of [
          "business_name",
          "business_nature",
          "registration_date",
          "business_address",
          "registration_number",
          "registration_country",
        ]) {
          delete payload[key];
        }
      }

      // Remove individual-specific fields if account type IS business
      if (payload.account_type === "business") {
        for (const key of ["first_name", "last_name", "middle_name"]) {
          delete payload[key];
        }
      }

      // Ensure first_name and last_name are always in payload (empty string if not provided)
      if (!payload.first_name) {
        payload.first_name = "";
      }
      if (!payload.last_name) {
        payload.last_name = "";
      }

      // Remove optional/empty fields (except first_name, last_name which should always be present)
      for (const key of [
        "other_name",
        "postcode",
        "address",
        "flat",
        "email",
      ]) {
        if (!payload[key]) {
          delete payload[key];
        }
      }

      // Only remove middle_name if it's empty (for both individual and business)
      if (!payload.middle_name) {
        delete payload.middle_name;
      }

      if (payload.bank_name === "other") {
        payload.bank_name = payload.other_name;
      }

      try {
        if (id) {
          // Update existing recipient
          if (PrevEmail === payload.email) delete payload.email;
          let response = await updateUserRecipient(id, payload);
          setLoading(false);

          if (response) {
            if (response.code === "200") {
              toast.success("Successfully updated", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: true,
              });
              navigate("/user-recipients");
            } else {
              toast.error(response?.message, {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: true,
              });
              // Stay on the form to show error - don't navigate
            }
          }
        } else {
          // Create new recipient
          let response = await createRecipient({ ...payload });
          setLoading(false);

          if (response) {
            if (response.code === "200") {
              toast.success("Successfully added new recipient", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: true,
              });

              if (section !== "transfer") {
                navigate("/user-recipients");
              } else {
                nextAction(values, response.data);
              }
            } else {
              // Error case - show error and handle based on section
              toast.error(response?.message, {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: true,
              });

              // Only call backHandler for transfer section
              // For regular recipient creation, stay on form to show error
              // if (section === "transfer") {
              //   backHandler();
              // }
              // If section !== "transfer", don't navigate - stay on form
            }
          } else {
            // Handle case where response is null/undefined
            toast.error("An error occurred while creating the recipient", {
              position: "bottom-right",
              autoClose: 2000,
              hideProgressBar: true,
            });

            if (section === "transfer") {
              backHandler();
            }
            // Stay on form for regular recipient creation
          }
        }
      } catch (error) {
        // Handle any unexpected errors
        setLoading(false);
        toast.error("An unexpected error occurred", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: true,
        });

        if (section === "transfer") {
          backHandler();
        }
        // Stay on form for regular recipient creation
      }
    },
  });
useEffect(() => {
  if (!!id && bankList.length > 0) { // Wait for bankList to be loaded
    const dataFetch = async () => {
      setLoading(true);
      const response = await getUserRecipient(id);
      if (response && response.code === "200") {
        const values = response.data;
        if (values.country === "Nigeria") {
          const find = bankList.find(
            (bank) => bank.label === values.bank_name
          );
          if (!find) {
            values.bank_name = "other";
            values.other_name = values.bank_name;
          }
        }
        setPrevEmail(values.email || "");
        setValues(values);
        setPrevAcc(values.account_number);
      }
      setLoading(false);
    };
    dataFetch();
  }
}, [id, bankList]); 
useEffect(() => {
  const fetchAccountTypes = async () => {
    const response = await getCustomerTypes();
    if (response && response.code === "200") {
      setAccountTypes(response.data);
    }
  };
  
  const fetchBankList = async () => {
    const response = await getBankList();
    if (response && response.code === "200") {
      const transformedBanks = response.data.data.map((bank) => ({
        label: bank.name,
        value: bank.name,
        code: bank.code,
      }));
      setBankList(transformedBanks);
    } else {
      setBankList(Bank_list);
    }
    setLoadingBanks(false);
  };
  
  fetchAccountTypes();
  fetchBankList();
}, []); // Empty dependency - runs once on mount

  // Bank selector customizers
  const customStyles = {
    control: (base) => ({
      ...base,
      borderColor:
        errors.bank_name && touched.bank_name ? "red" : base.borderColor,
    }),
  };
  const Placeholder = (props) => {
    return <components.Placeholder {...props} />;
  };

  // Handler to control the input change and validation using inputValidator hook
  const changeHandler = (e) => {
    inputValidator(e, handleChange);
  };

  // Function to valildate account number and check if it's pre-existing ?
  const validateAccount = (e) => {
    if (!!e.target.value && prev_account_number !== e.target.value) {
      checkExistingAccount({ account_number: e.target.value }).then((res) => {
        if (res.code === "400") {
          setExisting(true);
        } else {
          setExisting(false);
        }
      });
    }
    handleBlur(e);
  };

  //  Handler to control the phone input update and update address accordingly
  const handlePhone = (e, coun) => {
    let mobileWithoutDialCode = e
      .substring(coun.dialCode.length)
      .replace(/[^0-9]/, "");
    handleChange({
      target: { name: "mobile", value: coun.dialCode + mobileWithoutDialCode },
    });
    setFieldValue("country", coun.name);
    setDialCode(coun.dialCode);
    if (values.country_code.toLowerCase() != coun.countryCode.toLowerCase()) {
      handleChange({
        target: { name: "country_code", value: coun.countryCode.toLowerCase() },
      });
      handleReset();
    }
  };

  const countryHandler = (e) => {
    const selected = countryList.find((country) => {
      return country.name === e.target.value;
    });
    handleChange(e);
    if (selected) {
      setFieldValue("country_code", selected.code);
    }
    handleReset();
  };

  // function to reset fields on countrycode change
  const handleReset = () => {
    const clear_fields = [
      "street",
      "state",
      "city",
      "postcode",
      "building",
      "flat",
      "address",
    ];
    clear_fields.forEach((field) => {
      setFieldValue(field, "");
    });
  };

  // function to handle the cancel operation
  const handleCancel = () => {
    if (section === "transfer") {
      backHandler();
    } else {
      navigate("/user-recipients");
    }
  };

  return (
    <>
      {loading && (
        <>
          <div className="loader-overly">
            <div className="loader"></div>
          </div>
        </>
      )}
      <section className="showrecepient">
        <div className="form-head mb-4">
          <h2 className="text-black font-w600 mb-0">
            <b>{id ? "Update" : "Add"} Recipient</b>
            {section !== "transfer" && (
              <NavLink to="/user-recipients">
                <button className="start-form-button back-btn">
                  <MdOutlineKeyboardBackspace />
                  Back
                </button>
              </NavLink>
            )}
          </h2>
        </div>
        <form noValidate className="single-recipient" onSubmit={handleSubmit}>
          <div className="card">
            <div className="card-body">
              {/*  Account type */}
              <div className="row each-row">
                <h5>Account Type</h5>
                <div className="col-md-6 col-lg-6 col-sm-12">
                  {!!id ? (
                    <input
                      type="text"
                      name="account_type"
                      id="account_type"
                      className={clsx(
                        "form-control bg-transparent text-capitalize"
                      )}
                      value={values.account_type}
                      disabled
                      readOnly
                    />
                  ) : (
                    <Form.Select
                      name="account_type"
                      value={values.account_type}
                      onChange={handleChange}
                      className="form-control bg-transparent text-capitalize"
                      id="account_type"
                    >
                      {account_types.map((type, index) => (
                        <option
                          value={type.value}
                          key={index}
                          className="text-capitalize"
                        >
                          {type.text}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </div>
              </div>

              {/*  Business Information */}
              {values.account_type === "business" && (
                <>
                  <div className="row each-row">
                    <h5>Business Details</h5>
                    <div className="col-md-4 col-lg-4 col-sm-6">
                      <div className="input_field">
                        <p className="get-text">
                          Name of business
                          <span style={{ color: "red" }}>*</span>
                        </p>
                        <input
                          type="text"
                          name="business_name"
                          id="business_name"
                          value={values.business_name}
                          onChange={changeHandler}
                          maxLength={limits.business_name.max}
                          className={clsx(
                            "form-control bg-transparent",
                            {
                              "is-invalid":
                                touched.business_name && errors.business_name,
                            },
                            {
                              "is-valid":
                                touched.business_name && !errors.business_name,
                            }
                          )}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/*  Bank information */}
              <div className="row each-row">
                <h5>Bank Information</h5>
                <div className="col-md-4">
                  <div className="input_field">
                    <p className="get-text">
                      Bank Name<span className="text-danger">*</span>
                    </p>
                    {currency === "NGN" ? (
                      <Select
                        isLoading={loadingBanks}
                        isDisabled={loadingBanks}
                        options={bankList}
                        onChange={(e) => {
                          handleChange({
                            target: { name: "bank_name", value: e.value },
                          });
                          setSelectedBankCode(e.code || "");
                        }}
                        value={
                          values.bank_name !== null
                            ? {
                              label: values.bank_name,
                              value: values.bank_name,
                            }
                            : ""
                        }
                        name="bank_name"
                        styles={customStyles}
                        components={{ Placeholder }}
                        placeholder={loadingBanks ? "Loading banks..." : "Select bank"}
                        onBlur={handleBlur}
                      />
                    ) : (
                      // <input
                      //   type="text"
                      //   name="bank_name"
                      //   value={values?.bank_name}
                      //   onChange={changeHandler}
                      //   maxLength={50}
                      //   className={clsx(
                      //     "form-control bg-transparent",
                      //     {
                      //       "is-invalid": touched.bank_name && errors.bank_name,
                      //     },
                      //     {
                      //       "is-valid": touched.bank_name && !errors.bank_name,
                      //     }
                      //   )}
                      //   onBlur={handleBlur}
                      // />
                      (
                        <div className="position-relative">
                          <input
                            type="text"
                            name="bank_name"
                            value={values?.bank_name}
                            onChange={(e) => {
                              const { value } = e.target;
                              changeHandler(e);
                              if (value.length > 0) {
                                const filtered = bankList.filter((bank) =>
                                  bank.label.toLowerCase().includes(value.toLowerCase())
                                );
                                setSuggestions(filtered);
                              } else {
                                setSuggestions([]);
                              }
                            }}
                            maxLength={50}
                            className={`form-control bg-transparent 
          ${touched.bank_name && errors.bank_name ? "is-invalid" : ""}
          ${touched.bank_name && !errors.bank_name ? "is-valid" : ""}`}
                            onBlur={() => {
                              // const selected = bankList.find((e) => {
                              //   e.label === values.bank_name
                              // })

                              // if (!selected) setValues((prev) => {
                              //   return {
                              //     ...prev,
                              //     bank_name: ""
                              //   }
                              // })

                              setTimeout(() => {
                                setSuggestions([])
                              }, 150);
                            }}
                            autoComplete="off"
                          />

                          {suggestions.length > 0 && (
                            <ul
                              className="list-group position-absolute w-100"
                              style={{ zIndex: 1000, maxHeight: "200px", overflowY: "auto" }}
                            >
                              {suggestions.map((bank, i) => (
                                <li
                                  key={i}
                                  className="list-group-item list-group-item-action"
                                  onClick={() => {
                                    setValues((prev) => {
                                      return {
                                        ...prev,
                                        bank_name: bank.label
                                      }
                                    })
                                    setSuggestions([])
                                  }}
                                  style={{ cursor: "pointer" }}
                                >
                                  {bank.label}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
                {currency === "NGN" && values.bank_name === "other" && (
                  <div className="col-md-4">
                    <div className="input_field">
                      <p className="get-text">
                        Other Bank Name<span style={{ color: "red" }}>*</span>
                      </p>
                      <input
                        type="text"
                        name="other_name"
                        value={values?.other_name}
                        onChange={changeHandler}
                        maxLength={50}
                        className={clsx(
                          "form-control bg-transparent",
                          {
                            "is-invalid":
                              touched.other_name && errors.other_name,
                          },
                          {
                            "is-valid":
                              touched.other_name && !errors.other_name,
                          }
                        )}
                        onBlur={handleBlur}
                      />
                    </div>
                  </div>
                )}
                <div className="col-md-4">
                  <div className="input_field">
                    <p className="get-text">
                      Account number<span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="text"
                      name="account_number"
                      value={values?.account_number}
                      onChange={changeHandler}
                      maxLength={30}
                      onBlur={(e) => validateAccount(e)}
                      className={clsx(
                        "form-control bg-transparent",
                        {
                          "is-invalid":
                            (touched.account_number && errors.account_number) ||
                            is_account_existing,
                        },
                        {
                          "is-valid":
                            touched.account_number && !errors.account_number,
                        }
                      )}
                    />
                    {is_account_existing && (
                      <p className="text-danger fs-6 mt-1 ms-2">
                        Recipient with this account number already exists!
                      </p>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="input_field">
                    <p className="get-text">
                      Bank Identifier (BIC, IFSC, etc) (optional)
                    </p>
                    <input
                      type="text"
                      name="bank_identifier"
                      value={values?.bank_identifier}
                      onChange={changeHandler}
                      maxLength={limits.bank_identifier}
                      onBlur={(e) => validateAccount(e)}
                      className={clsx(
                        "form-control bg-transparent",
                        {
                          "is-invalid":
                            values.bank_identifier &&
                            touched.bank_identifier &&
                            errors.bank_identifier,
                        },
                        {
                          "is-valid":
                            values.bank_identifier &&
                            touched.bank_identifier &&
                            !errors.bank_identifier,
                        }
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Recipient Details */}
              {values.account_type === "individual" && (
                <div className="row each-row">
                  <h5>Recipient Details</h5>
                  <div className="col-md-4">
                    <div className="input_field">
                      <p className="get-text">
                        First Name<span style={{ color: "red" }}>*</span>
                      </p>
                      <input
                        type="text"
                        name="first_name"
                        value={values.first_name}
                        onChange={changeHandler}
                        onBlur={handleBlur}
                        maxLength={limits.name}
                        className={clsx(
                          "form-control bg-transparent",
                          {
                            "is-invalid":
                              touched.first_name && errors.first_name,
                          },
                          {
                            "is-valid":
                              touched.first_name && !errors.first_name,
                          }
                        )}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="input_field">
                      <p className="get-text">Middle Name (optional)</p>
                      <input
                        type="text"
                        name="middle_name"
                        value={values.middle_name}
                        onChange={changeHandler}
                        onBlur={handleBlur}
                        maxLength={limits.name}
                        className={clsx(
                          "form-control bg-transparent",
                          {
                            "is-invalid":
                              touched.middle_name &&
                              errors.middle_name &&
                              values.middle_name !== "",
                          },
                          {
                            "is-valid":
                              touched.middle_name &&
                              !errors.middle_name &&
                              values.middle_name !== "",
                          }
                        )}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="input_field">
                      <p className="get-text">
                        Last Name<span style={{ color: "red" }}>*</span>
                      </p>
                      <input
                        type="text"
                        name="last_name"
                        value={values.last_name}
                        onChange={changeHandler}
                        onBlur={handleBlur}
                        maxLength={limits.name}
                        className={clsx(
                          "form-control bg-transparent",
                          {
                            "is-invalid": touched.last_name && errors.last_name,
                          },
                          { "is-valid": touched.last_name && !errors.last_name }
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="row each-row">
                <div className="col-md-4">
                  <div className="input_field">
                    <p className="get-text">Email (optional)</p>
                    <input
                      type="text"
                      name="email"
                      value={values.email}
                      onChange={changeHandler}
                      onBlur={handleBlur}
                      maxLength={limits.email.max}
                      className={clsx(
                        "form-control bg-transparent",
                        {
                          "is-invalid":
                            touched.email && errors.email && values.email,
                        },
                        {
                          "is-valid":
                            touched.email && !errors.email && values.email,
                        }
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Address Details */}
              <div className="row each-row">
                <h5>Address</h5>
                <div className="col-md-4 mb-3" id="country">
                  <Form.Group className="form_label">
                    <p className="get-text">
                      Country<span style={{ color: "red" }}>*</span>
                    </p>
                    <select
                      value={values.country}
                      name="country"
                      onChange={countryHandler}
                      className="form-control form-select bg-transparent"
                    >
                      {countryList.map((country) => {
                        return (
                          <option key={country.code} value={country.name}>
                            {country.name}
                          </option>
                        );
                      })}
                    </select>
                  </Form.Group>
                </div>
                <div className="col-md-12 mb-3" id="Address">
                  <Form.Group className="form_label" controlId="Address">
                    <p className="get-text">Address</p>
                    <Autocomplete
                      apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                      onPlaceSelected={(value) => {
                        getSelectedStreet(value, setFieldValue);
                      }}
                      placeholder="Street Address, Company or P.O. box.. "
                      id="address"
                      name="address"
                      className="form-control"
                      options={{
                        types: [],
                        componentRestrictions: {
                          country: values?.country_code?.toLowerCase(),
                        },
                      }}
                      onChange={handleChange}
                      value={values.address}
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="row each-row">
                <div className="col-md-4 mb-3" id="flat">
                  <Form.Group className="form_label" controlId="flat">
                    <p className="get-text">Unit/Apt No.</p>
                    <input
                      type="text"
                      name="flat"
                      value={values.flat}
                      onChange={handleChange}
                      max={limits.flat}
                      onBlur={handleBlur}
                      className={clsx(
                        "form-control bg-transparent",
                        {
                          "is-invalid":
                            touched.flat && errors.flat && values.flat !== "",
                        },
                        {
                          "is-valid":
                            touched.flat && !errors.flat && values.flat !== "",
                        }
                      )}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-4 mb-3" id="build">
                  <Form.Group className="form_label" controlId="build">
                    <p className="get-text">
                      Building No.<span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="text"
                      name="building"
                      value={values.building}
                      onChange={handleChange}
                      max={limits.building}
                      onBlur={handleBlur}
                      className={clsx(
                        "form-control bg-transparent",
                        { "is-invalid": touched.building && errors.building },
                        {
                          "is-valid": touched.building && !errors.building,
                        }
                      )}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-4 mb-3" id="street">
                  <Form.Group className="form_label" controlId="street">
                    <p className="get-text">
                      Street Name<span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="text"
                      name="street"
                      value={values.street}
                      onChange={handleChange}
                      max={limits.street}
                      onBlur={handleBlur}
                      className={clsx(
                        "form-control bg-transparent",
                        { "is-invalid": touched.street && errors.street },
                        {
                          "is-valid": touched.street && !errors.street,
                        }
                      )}
                    />
                  </Form.Group>
                </div>
              </div>
              <div className="row each-row">
                <div className="col-md-4 mb-3" id="city">
                  <Form.Group className="form_label">
                    <p className="get-text">
                      City/Suburb<span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="text"
                      name="city"
                      value={values.city}
                      onChange={changeHandler}
                      max={limits.city}
                      onBlur={handleBlur}
                      className={clsx(
                        "form-control bg-transparent",
                        { "is-invalid": touched.city && errors.city },
                        {
                          "is-valid": touched.city && !errors.city,
                        }
                      )}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-4 mb-3" id="zip">
                  <Form.Group className="form_label">
                    <p className="get-text">
                      Zip/Postal Code
                      {isAfrican === true ? (
                        <></>
                      ) : (
                        <span style={{ color: "red" }}>*</span>
                      )}
                    </p>
                    <input
                      type="text"
                      name="postcode"
                      value={values.postcode}
                      onChange={changeHandler}
                      maxLength={limits.other_zip || 10}
                      onBlur={handleBlur}
                      className={clsx(
                        "form-control bg-transparent",
                        {
                          "is-invalid":
                            touched.postcode &&
                            errors.postcode &&
                            (values.postcode !== "" || undefined || null),
                        },
                        {
                          "is-valid":
                            touched.postcode &&
                            !errors.postcode &&
                            (values.postcode !== "" || undefined || null),
                        }
                      )}
                    />

                  </Form.Group>
                </div>
                <div className="col-md-4 mb-3" id="state">
                  <Form.Group className="form_label">
                    <p className="get-text">
                      State<span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="text"
                      name="state"                                               
                      value={values.state}
                      onChange={handleChange}
                      max={limits.state}
                      onBlur={handleBlur}
                      className={clsx(
                        "form-control bg-transparent",
                        { "is-invalid": touched.state && errors.state },
                        {
                          "is-valid": touched.state && !errors.state,
                        }
                      )}
                    />
                  </Form.Group>
                </div>
              </div>
              {/* Action Buttons */}
              <div className="row">
                <div className="col-md-4">
                  <button
                    type="button"
                    className="start-form-button full-col"
                    onClick={() => handleCancel()}
                  >
                    {section === "transfer" ? "Back" : "Cancel"}
                  </button>
                </div>
                <div className="col-md-8 full-col">
                  <button
                    className="form-button"
                    style={is_account_existing ? { cursor: "not-allowed" } : {}}
                    disabled={is_account_existing}
                  >
                    {section !== "transfer" ? (
                      <>{id ? "Update" : "Create"} Recipient</>
                    ) : (
                      <>Save & Continue</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </section >
    </>
  );
};

export default RecipientForm;
